import React from 'react';
import { Text } from '@/components/gluestack/text';
import dayjs from '@/i18n/dayjs-config';

interface MessageTimeProps {
  date: string | Date;
  className?: string;
}

/**
 * Component to display time in a Facebook Messenger-style format:
 * - Show time if it's in the last 24h (e.g., "2:30 PM")
 * - Show weekday if it's older than 24h but within the week (e.g., "Tue")
 * - Show Month and Day if it's within the last year (e.g., "May 22")
 * - Show full Month Day, Year if it's older than a year (e.g., "Sep 27, 2024")
 */
export function MessageTime({ date, className = '' }: MessageTimeProps) {
  // Ensure we have a valid dayjs object regardless of input type
  const dateObj = typeof date === 'string' ? dayjs(date.endsWith('Z') ? date : date + 'Z') : dayjs(date);
  const now = dayjs();
  
  const diffHours = now.diff(dateObj, 'hour');
  const diffDays = now.diff(dateObj, 'day');
  const diffYears = now.diff(dateObj, 'year');
  
  let formattedTime: string;
  
  if (diffHours < 24) {
    // Less than 24 hours - show time (e.g., "2:30 PM")
    formattedTime = dateObj.format('LT'); // Localized time format
  } else if (diffDays < 7) {
    // Less than a week - show weekday (e.g., "Tue")
    formattedTime = dateObj.format('ddd'); // Short weekday name
  } else if (diffYears < 1) {
    // Less than a year - show month and day (e.g., "May 22")
    formattedTime = dateObj.format('MMM D'); // Short month name and day
  } else {
    // More than a year - show full date (e.g., "Sep 27, 2024")
    formattedTime = dateObj.format('MMM D, YYYY'); // Short month name, day, and year
  }

  return <Text className={className} isTruncated>{formattedTime}</Text>;
} 